// The fog field the parchment layer reads: one byte per hex, 0 = fresh … 255 = no recent source.
//
// WorldSnapshot.staleness is 255 on every unowned hex (open sea, Antarctica), because nothing
// sources them. Fogging all of them would bury the oceans, so the field carries the staleness of
// OWNED hexes only and lets it bleed a hex or two into the unowned hexes beside them, fading out:
// a stale country reads as a region of old map that frays into the sea instead of a hex-cut block.
// The bleed is decoration: it states nothing about the sea it covers.
//
// The reach scales with the island: a lone stale atoll (Kiribati, Marshall Islands) bleeds one
// hex, a landmass of sixteen hexes or more the full BLEED_STEPS. Six hexes of parchment around a
// one-hex island read as a beige explosion at world zoom.
//
//   owned, stale (200)  ─ 1 hex ─►  120  ─ 2 hexes ─►  72  ─► (BLEED_STEPS: stop)

import { HEX_COUNT, neighbour } from '../../core/grid.ts';

/** Most hexes the fog bleeds into the unowned hexes beside a stale one. */
export const BLEED_STEPS = 2;
/** Share of the value that survives each step of the bleed. */
export const BLEED_KEEP = 0.6;
/** Values below this are dropped: the bleed ends. */
export const FOG_MIN = 36;
const DIRECTIONS = 6;
const NO_HEX = -1;

/** Hexes the fog bleeds out of a connected territory of `size` hexes: floor(sqrt(size)), capped. */
export function bleedSteps(size: number): number {
  return Math.min(BLEED_STEPS, Math.floor(Math.sqrt(size)));
}

/** The owners the last flood saw (a copy) and what it found: most snapshots repeat the owners of the one before. */
let floodedOwner: Uint16Array | null = null;
let floodedReach: Uint8Array | null = null;

function sameOwners(a: Uint16Array, b: Uint16Array): boolean {
  for (let i = 0; i < HEX_COUNT; i++) {
    if (a[i] !== b[i]) {
      return false;
    }
  }
  return true;
}

/** A copy of the reach of every owned hex (the bleed consumes it), from the flood or its cache. */
function reachOf(owner: Uint16Array): Uint8Array {
  if (floodedOwner && floodedReach && sameOwners(floodedOwner, owner)) {
    return floodedReach.slice();
  }
  const reach = floodIslands(owner);
  floodedOwner = owner.slice();
  floodedReach = reach.slice();
  return reach;
}

/** Bleed reach of every owned hex: that of its connected island of one owner. */
function floodIslands(owner: Uint16Array): Uint8Array {
  const reach = new Uint8Array(HEX_COUNT);
  const seen = new Uint8Array(HEX_COUNT);
  for (let start = 0; start < HEX_COUNT; start++) {
    if (owner[start] === 0 || seen[start]) {
      continue;
    }

    // Flood the island, remembering its hexes so they can all take its reach.
    const island = [start];
    seen[start] = 1;
    for (let at = 0; at < island.length; at++) {
      for (let d = 0; d < DIRECTIONS; d++) {
        const n = neighbour(island[at], d);
        if (n === NO_HEX || seen[n] || owner[n] !== owner[start]) {
          continue;
        }
        seen[n] = 1;
        island.push(n);
      }
    }

    const steps = bleedSteps(island.length);
    for (const hex of island) {
      reach[hex] = steps;
    }
  }
  return reach;
}

/** Staleness of owned hexes, bled into unowned neighbours; unowned hexes far from any owner are 0. */
export function buildFogField(staleness: Uint8Array, owner: Uint16Array): Uint8Array {
  const field = new Uint8Array(HEX_COUNT);
  const left = reachOf(owner);
  let frontier: number[] = [];
  for (let i = 0; i < HEX_COUNT; i++) {
    if (owner[i] === 0) {
      continue;
    }
    field[i] = staleness[i];
    if (staleness[i] >= FOG_MIN) {
      frontier.push(i);
    }
  }

  for (let step = 0; step < BLEED_STEPS && frontier.length > 0; step++) {
    const next: number[] = [];
    for (const hex of frontier) {
      const carried = Math.round(field[hex] * BLEED_KEEP);
      if (carried < FOG_MIN || left[hex] === 0) {
        continue;
      }
      for (let d = 0; d < DIRECTIONS; d++) {
        const n = neighbour(hex, d);
        if (n < 0 || owner[n] !== 0 || field[n] >= carried) {
          continue;
        }
        field[n] = carried;
        left[n] = left[hex] - 1;
        next.push(n);
      }
    }
    frontier = next;
  }
  return field;
}

/** A field part-way from `from` to `to` (t 0..1), for a snapshot that arrives mid-transition. */
export function blendField(from: Uint8Array, to: Uint8Array, t: number): Uint8Array {
  const out = new Uint8Array(HEX_COUNT);
  for (let i = 0; i < HEX_COUNT; i++) {
    out[i] = Math.round(from[i] + (to[i] - from[i]) * t);
  }
  return out;
}
